import { Dialog } from "@/design-system/Dialog/Dialog";
import { resetTooltip } from "@repo/shared/credits";
import { Trans, useTranslation } from "@repo/shared/i18n";
import type { CreditFeatureId } from "@repo/shared/redux";
import {
  dailyLimitSelector,
  dailyRemainingSelector,
  dailyResetsAtSelector,
} from "@repo/shared/redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { CreditIcon } from "./CreditIcon";
import { DailyLimitPhrase } from "./DailyLimitPhrase";
import { EverythingElseFree } from "./EverythingElseFree";
import { useCreditDialogTracking } from "./useCreditDialogTracking";

/** The metered features, named as `CREDIT_FEATURES[id].label` used to name them. */
const PRICED_FEATURES: CreditFeatureId[] = [
  "solve-sbc-club",
  "build-squad-club",
  "squad-recommendations-club",
  "evo-chains-club",
  "gallery-solutions-built",
  "gallery-level-plan-built",
];

/**
 * The first — and, if we get it right, only — explanation of credits.
 *
 * Raised by useCreditsIntro when a new user's sign-up bonus runs out, which is
 * the moment the nav-bar balance and per-feature costs stop being hidden. Framed
 * around what stays free, because the user has just been using the product for
 * free and nothing about that has actually changed.
 */
export const CreditsIntroDialog = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation("subscription");
  const navigate = useNavigate();
  const dailyRemaining = useSelector(dailyRemainingSelector);
  const dailyLimit = useSelector(dailyLimitSelector);
  const resetsAt = useSelector(dailyResetsAtSelector);
  const { trackExit, trackLearnMoreClick } = useCreditDialogTracking({
    context: "used-bonus-credits",
  });

  return (
    <Dialog
      isOpen
      onClose={() => {
        trackExit("negative");
        onClose();
      }}
      title={t("credits.intro.title")}
      width={520}
      negLabel={t("credits.intro.gotIt")}
      onNegative={() => trackExit("negative")}
      /* Not "See plans": there is one plan. And it lands on /subscription
         rather than checkout, so it can't promise an upgrade either. The
         `from` state scrolls that page to its credits explainer — "learn
         more" has to answer the question this dialog just raised, not open
         on pricing. */
      posLabel={t("credits.intro.learnMore")}
      onPositive={() => {
        trackLearnMoreClick();
        navigate("/subscription", { state: { from: "credits" } });
      }}
    >
      <p>
        <Trans
          ns="subscription"
          i18nKey="credits.intro.spent"
          components={{ daily: <DailyLimitPhrase limit={dailyLimit} /> }}
        />
        {typeof dailyRemaining === "number" && (
          <>
            {" "}
            <Trans
              ns="subscription"
              i18nKey="credits.intro.remaining"
              values={{
                remaining: dailyRemaining,
                noun: t("credits.noun", { count: dailyRemaining }),
              }}
              components={{
                b: (
                  <span
                    className="font-bold text-foreground"
                    {...(resetsAt ? { title: resetTooltip(resetsAt) } : {})}
                  />
                ),
              }}
            />
          </>
        )}
      </p>

      <p className="mt-3.5 font-bold text-foreground">
        {t("credits.intro.onlyFor")}
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {PRICED_FEATURES.map((id) => (
          <li key={id} className="flex items-center gap-2">
            <CreditIcon />
            {t(`credits.features.${id}`)}
          </li>
        ))}
      </ul>

      <EverythingElseFree className="mt-3.5" />
    </Dialog>
  );
};
